import PropTypes from 'prop-types';

import { cn } from '#/lib/utils';
import FloatingDateInput from '#/modules/commonComponents/form/FloatingDateInput';
import FloatingTextarea from '#/modules/commonComponents/form/FloatingTextarea';
import { SelectCore } from '#/modules/commonComponents/form/select';
import {
  Tabs,
  TabsList,
  TabsTrigger,
} from '#/modules/commonComponents/tabs';
import { TRANSFER_CONFIG_MODE } from '#/modules/momMgmt/workOrder/transfer/helper/transferConfigMode';

import WorkOrderTransferWorkOrderField from './WorkOrderTransferWorkOrderField';

const MODE_HINT = {
  [TRANSFER_CONFIG_MODE.BY_TARGET_WAREHOUSE]:
    '選目標倉後載入該倉所有工單的需求彙總。按物料開選庫（僅他倉可轉入庫存）。',
  [TRANSFER_CONFIG_MODE.BY_WORK_ORDER]:
    '選一張工單，目標倉自動帶入該工單工作倉，需求量改為該工單需求。按物料開選庫（僅他倉可轉入庫存）。',
};

// 選中態用 !：Radix 選中是 data-state=active，且需壓過 hover:text-foreground 與 theme-dark.css 的分頁規則
const MODE_TRIGGER_CLASS = `
  flex-1 px-4 py-1.5
  @xl/sheet:flex-none
  data-[state=active]:border-transparent!
  data-[state=active]:bg-primary! data-[state=active]:text-primary-foreground!
  dark:data-[state=active]:bg-primary-400! dark:data-[state=active]:text-white!
`;

const WorkOrderTransferHeader = ({
  warehouseOptions = [],
  targetWarehouseId,
  onTargetWarehouseChange,
  transferDate = '',
  onTransferDateChange,
  issueReason = '',
  onIssueReasonChange,
  remark = '',
  onRemarkChange,
  isWarehouseLoading = false,
  configMode = TRANSFER_CONFIG_MODE.BY_TARGET_WAREHOUSE,
  onConfigModeChange,
  selectedWorkOrder = null,
  onOpenWorkOrderPicker,
  onClearWorkOrder,
}) => {
  const isWorkOrderMode = configMode === TRANSFER_CONFIG_MODE.BY_WORK_ORDER;
  // 指定工單：目標倉由工單工作倉帶入並鎖定；工單無工作倉時才開放手選
  const isTargetLockedByWorkOrder = isWorkOrderMode
    && (!selectedWorkOrder?.id || Boolean(selectedWorkOrder?.workWarehouseId));

  return (
    <section>
      <div className="mb-5">
        <h2 className="form-block__title">操作設定</h2>
        <p className="mt-1 text-xs text-muted-foreground">
          {MODE_HINT[configMode]}
        </p>
      </div>
      <div className="mb-5 flex flex-col gap-2 @xl/sheet:flex-row @xl/sheet:items-center @xl/sheet:gap-3">
        <span id="transferConfigModeLabel" className="text-sm text-muted-foreground">
          配置模式
        </span>
        <Tabs
          value={configMode}
          onValueChange={(next) => onConfigModeChange?.(next)}
          className="w-full @xl/sheet:w-auto"
        >
          <TabsList
            aria-labelledby="transferConfigModeLabel"
            className="w-full @xl/sheet:w-auto"
          >
            <TabsTrigger
              value={TRANSFER_CONFIG_MODE.BY_TARGET_WAREHOUSE}
              className={MODE_TRIGGER_CLASS}
            >
              依目標倉
            </TabsTrigger>
            <TabsTrigger
              value={TRANSFER_CONFIG_MODE.BY_WORK_ORDER}
              className={MODE_TRIGGER_CLASS}
            >
              指定工單
            </TabsTrigger>
          </TabsList>
        </Tabs>
      </div>
      <div className="space-y-3">
        <div
          className={cn(
            'grid grid-cols-1 gap-3',
            isWorkOrderMode ? '@4xl/sheet:grid-cols-3' : '@4xl/sheet:grid-cols-2',
          )}
        >
          {isWorkOrderMode ? (
            <WorkOrderTransferWorkOrderField
              id="transferWorkOrder"
              workOrder={selectedWorkOrder}
              onOpen={() => onOpenWorkOrderPicker?.()}
              onClear={() => onClearWorkOrder?.()}
            />
          ) : null}
          <SelectCore
            id="transferTargetWarehouse"
            label={isWorkOrderMode ? '目標倉（依工單工作倉）' : '目標倉'}
            tip={isTargetLockedByWorkOrder ? '選工單後自動帶入該工單的工作倉' : undefined}
            options={warehouseOptions}
            value={targetWarehouseId}
            onChange={onTargetWarehouseChange}
            isDisabled={isWarehouseLoading || isTargetLockedByWorkOrder}
            noOptionsMessage="無可用倉別"
          />
          <FloatingDateInput
            id="transferDate"
            label="轉倉日"
            value={transferDate}
            onChange={onTransferDateChange}
            containerClassName="mb-0!"
          />
        </div>
        <div className="grid grid-cols-1 gap-3 @4xl/sheet:grid-cols-2">
          <FloatingTextarea
            id="issueReason"
            label="轉倉事由"
            value={issueReason}
            onChange={(event) => onIssueReasonChange(event.target.value)}
          />
          <FloatingTextarea
            id="remark"
            label="備註"
            value={remark}
            onChange={(event) => onRemarkChange(event.target.value)}
          />
        </div>
      </div>
    </section>
  );
};

WorkOrderTransferHeader.propTypes = {
  warehouseOptions: PropTypes.arrayOf(PropTypes.shape({
    value: PropTypes.string,
    label: PropTypes.string,
  })),
  targetWarehouseId: PropTypes.string,
  onTargetWarehouseChange: PropTypes.func.isRequired,
  transferDate: PropTypes.string,
  onTransferDateChange: PropTypes.func.isRequired,
  issueReason: PropTypes.string,
  onIssueReasonChange: PropTypes.func.isRequired,
  remark: PropTypes.string,
  onRemarkChange: PropTypes.func.isRequired,
  isWarehouseLoading: PropTypes.bool,
  configMode: PropTypes.oneOf(Object.values(TRANSFER_CONFIG_MODE)),
  onConfigModeChange: PropTypes.func,
  selectedWorkOrder: PropTypes.shape({
    id: PropTypes.string,
    workOrderNo: PropTypes.string,
    workWarehouseId: PropTypes.string,
  }),
  onOpenWorkOrderPicker: PropTypes.func,
  onClearWorkOrder: PropTypes.func,
};

export default WorkOrderTransferHeader;
